import { postRunCode } from '@yandex-int/k-common/client/api/defs/post-run-code';
import b from 'b_';
import get from 'lodash/get';
import { ProblemLoadable as ProblemBase, Scale, HiddenRender } from 'platform-components';
import { PROBLEM_THEMES } from 'platform-components/constants';
import { CodingProblem } from 'platform-components/src/components/coding-problem/coding-problem';
import { ProblemCoding } from 'platform-components/src/components/coding-problem/types';
import { isCodingProblem } from 'platform-components/src/components/coding-problem/utils';
import Robot from 'platform-components/src/components/robot/robot';
import { RobotProblem, ROBOT_TABS } from 'platform-components/src/components/robot/robot.types';
import { ScaleProps } from 'platform-components/src/components/scale/scale';
import { isWebProblem } from 'platform-components/src/components/web-problem/utils';
import { WebProblem } from 'platform-components/src/components/web-problem/web-problem';
import { isWebserviceProblem } from 'platform-components/src/components/webservice-problem/utils';
import { WebserviceProblem } from 'platform-components/src/components/webservice-problem/webservice-problem';
import { ProblemProps } from 'platform-components/src/typings';
import { hasElementClosestMethod } from 'platform-components/utils';
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';

// @ts-ignore
import css from 'common.components/lab-problem/constants.scss';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import { ProblemWrapperContext } from 'common.components/problem-wrapper/context';
import { LessonProblemWithResult } from 'common.components/student-lesson-stat/typings';
import { isRobot } from 'store/problems/utils';

import { SCREEN_QUERY, PROBLEM_WIDTH, BACKGROUND_SIZE, BACKGROUND_WIDTH } from './constants';
import { getBackgroundCoverScaleCompensation, getDoesBackgroundCoverByHeight } from './utils';
import { useCodingSolveSession } from './use-coding-solve-session';

import './teacher-problem.scss';

type omittedProps = 'as';
type TeacherProblemScaleProps = Omit<ScaleProps, omittedProps>;
export type TeacherProblemProps = Omit<ProblemProps<{ markerId: number; markerAnswer: any }>, 'theme'>;

export interface OwnProps extends TeacherProblemProps {
    itemLink?: LessonProblemWithResult;
    previewMode: boolean;
    robotInitialTab?: ROBOT_TABS;
    mix?: string;
    problemBlockPlateStyles?: object;
    readOnly: boolean;
    showCorrectAnswers: boolean;
    teacherMode: boolean;
    onScale?: (scaling: number) => void;
}

interface Props extends OwnProps {}

function useDisableProblemMarkdownLinks() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const preventDefault = (e: MouseEvent) => {
            if (hasElementClosestMethod(e.target)) {
                if (e.target.closest('.problem .markdown a')) {
                    e.preventDefault();
                }
            }
        };

        const { current } = ref;

        if (current) {
            current.addEventListener('click', preventDefault);

            return () => {
                current.removeEventListener('click', preventDefault);
            };
        }

        return undefined;
    }, []);

    return ref;
}

// eslint-disable-next-line complexity
export const TeacherProblem = (props: Props) => {
    const { onScale, previewMode, robotInitialTab, setAnswer = () => {}, ...restProps } = props;
    const [currentScaleValue, setCurrentScaleValue] = useState(0);
    const [problemWidth, setProblemWidth] = useState<number>();
    const isMobile = useMedia(SCREEN_QUERY.SMALL);
    const robotRef = useRef<HTMLDivElement | null>(null);
    const { wrapperRef } = useContext(ProblemWrapperContext);

    const handleMeasure = useCallback(
        ({ width }: { width: React.SetStateAction<number | undefined> }) => setProblemWidth(width),
        [setProblemWidth]
    );

    const contentAlignment = restProps.problem?.content_alignment;

    const problemBase = <ProblemBase {...(restProps as any)} theme={PROBLEM_THEMES.SCHOOL} />;

    const doesBackgroundCoverByHeight = wrapperRef?.current
        ? getDoesBackgroundCoverByHeight(wrapperRef.current, BACKGROUND_SIZE)
        : false;

    const problemToRenderHidden = contentAlignment && doesBackgroundCoverByHeight ? problemBase : null;

    // https://st.yandex-team.ru/EDUCATION-19409 костылим вот эту проблему
    // навешиваем листенер через addEventListener чтобы корректно работало с роутерными линками
    // которые вызывают navigate() вместо бразуреного перехода и не превентятся так же как браузерны
    // ссылки. Стоит выпилить, после хорошего продуктового решения
    const ref = useDisableProblemMarkdownLinks();

    if (isRobot(props.problem)) {
        const answer = get(props.problem, ['attempt', 'markers'], {});
        const robotProps = {
            ...restProps,
            previewMode,
            setAnswer,
            answer,
            initialTab: robotInitialTab,
            problem: props.problem as unknown as RobotProblem,
        };

        if (isMobile && !previewMode) {
            const height = robotRef.current?.getBoundingClientRect()?.height || 0;

            return (
                <div className="teacher-problem__robot-problem-scale-wrapper" ref={robotRef}>
                    <Scale minWidth={PROBLEM_WIDTH} onScale={(value) => setCurrentScaleValue(value)}>
                        <div
                            className="teacher-problem__robot-problem-wrapper"
                            style={{ height: height / currentScaleValue }}
                        >
                            <Robot
                                postRunCode={postRunCode}
                                {...robotProps}
                                // @ts-ignore Property 'attempt' does not exist on type 'RobotProblem'
                                attempt={props.problem.attempt}
                            />
                        </div>
                    </Scale>
                </div>
            );
        }

        return (
            <Robot
                postRunCode={postRunCode}
                {...robotProps}
                // @ts-ignore Property 'attempt' does not exist on type 'RobotProblem'
                attempt={props.problem.attempt}
            />
        );
    }

    if (isWebProblem(props.problem)) {
        return (
            <WebProblem
                {...props}
                // @ts-ignore Property 'attempt' does not exist on type 'Problem & ProblemWeb'.
                attempt={props.problem.attempt}
                problem={props.problem}
            />
        );
    }

    if (isWebserviceProblem(props.problem)) {
        return (
            <WebserviceProblem
                {...props}
                // @ts-ignore Property 'attempt' does not exist on type 'Problem & ProblemWeb'.
                attempt={props.problem.attempt}
                problem={props.problem}
            />
        );
    }

    if (isCodingProblem(props.problem)) {
        return <TeacherCodingProblem {...props} problem={props.problem} />;
    }

    const scale =
        contentAlignment && wrapperRef?.current
            ? getBackgroundCoverScaleCompensation(wrapperRef.current, BACKGROUND_SIZE)
            : 1;

    const getMinWidth = () => {
        if (!contentAlignment) {
            return PROBLEM_WIDTH;
        }

        if (problemWidth) {
            const paddingWidth = parseInt(css.cardProblemSidePadding, 10) * 2;

            return (problemWidth + paddingWidth) * scale;
        }

        if (doesBackgroundCoverByHeight) {
            return BACKGROUND_WIDTH * scale;
        }

        return BACKGROUND_WIDTH;
    };

    const minWidth = getMinWidth();
    const scaleProps: TeacherProblemScaleProps = {
        onScale,
        minWidth,
        // firefox по своему работает со scale, поэтому скрываем скролл
        mix: 'teacher-problem__scale-container',
    };

    if (contentAlignment && wrapperRef?.current) {
        if (doesBackgroundCoverByHeight) {
            Object.assign(scaleProps, {
                fitMode: true,
                width: minWidth,
                minWidth: undefined,
            });

            scaleProps.baseWidth = problemWidth ? problemWidth : BACKGROUND_WIDTH;
        } else {
            scaleProps.baseWidth = wrapperRef?.current.clientWidth;
        }
    }

    const isWaitingForProblemWidth = problemToRenderHidden && !problemWidth;

    const hiddenProblem = isWaitingForProblemWidth ? (
        <HiddenRender nodeRef={wrapperRef || undefined} onMeasure={handleMeasure} renderWidth={BACKGROUND_WIDTH}>
            {problemToRenderHidden}
        </HiddenRender>
    ) : null;

    return (
        <>
            {hiddenProblem}
            <Scale {...scaleProps}>
                <div
                    className={b('teacher-problem__problem-wrapper', {
                        alignment: contentAlignment,
                        hidden: isWaitingForProblemWidth,
                    })}
                    ref={ref}
                >
                    <ProblemBase {...(restProps as any)} setAnswer={setAnswer} theme={PROBLEM_THEMES.SCHOOL} />
                </div>
            </Scale>
        </>
    );
};

function TeacherCodingProblem(props: Props & { problem: ProblemCoding }) {
    const studentId = useSelector(lessonViewSelectors.getSelectedStudentId);
    const { isLoading, data: solveSession } = useCodingSolveSession({
        problemLinkId: props.itemLink?.id,
        studentId: studentId ?? undefined,
    });
    return (
        <CodingProblem
            {...props}
            // @ts-ignore Property 'attempt' does not exist on type 'Problem & ProblemCoding'.
            attempt={props.problem.attempt}
            // запрещаем выделение кода при показе правильных ответов
            isSelectBlocked={props.showCorrectAnswers}
            isSolveSessionLoading={isLoading}
            problem={props.problem}
            solveSession={solveSession ?? undefined}
        />
    );
}

TeacherProblem.displayName = 'TeacherProblem';

export default TeacherProblem;
